import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { ApiError } from '@/shared/api/errors';

import { type DocumentListParams, type DocumentUpload, documentKeys, documentsApi } from './api';
import type { Document, DocumentEdit, DocumentUrl } from './types';

export function useDocumentList(params: DocumentListParams) {
  return useQuery({
    queryKey: documentKeys.list(params),
    queryFn: () => documentsApi.list(params),
    placeholderData: keepPreviousData,
  });
}

export function useDocumentMonths() {
  return useQuery({ queryKey: documentKeys.months, queryFn: documentsApi.months });
}

export function useDocumentDetail(id: string | undefined) {
  return useQuery({
    queryKey: documentKeys.detail(id ?? ''),
    queryFn: () => documentsApi.detail(id ?? ''),
    enabled: Boolean(id),
  });
}

/** Every write of the archive refetches every list: a row moved, a count changed. */
function useArchiveWrite<TData, TVariables>(run: (variables: TVariables) => Promise<TData>) {
  const queryClient = useQueryClient();
  return useMutation<TData, ApiError, TVariables>({
    mutationFn: run,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
  });
}

export function useUploadDocument() {
  return useArchiveWrite<Document, DocumentUpload>(documentsApi.upload);
}

export function useReplaceDocument() {
  return useArchiveWrite<Document, { id: string; file: File }>(({ id, file }) =>
    documentsApi.replace(id, file),
  );
}

export function useEditDocument() {
  return useArchiveWrite<Document, { id: string; body: DocumentEdit }>(({ id, body }) =>
    documentsApi.edit(id, body),
  );
}

export function useTrashDocument() {
  return useArchiveWrite<Document, { id: string; reason: string }>(({ id, reason }) =>
    documentsApi.trash(id, reason),
  );
}

export function useRestoreDocument() {
  return useArchiveWrite<Document, string>((id) => documentsApi.restore(id));
}

/** Open the signed link in a new tab; when the browser blocks it, hand the link back. */
export function useOpenDocument() {
  const [blockedLink, setBlockedLink] = useState<DocumentUrl | null>(null);
  const mutation = useMutation<DocumentUrl, ApiError, { id: string; inline: boolean }>({
    mutationFn: ({ id, inline }) => documentsApi.url(id, inline ? 'inline' : 'attachment'),
    onMutate: () => {
      setBlockedLink(null);
    },
    onSuccess: (link) => {
      const opened = window.open(link.url, '_blank');
      if (opened) {
        opened.opener = null;
      } else {
        setBlockedLink(link);
      }
    },
  });
  return { mutation, blockedLink };
}
